import React from 'react';
import { Box, Typography } from '@mui/material';
import { STATUS_COLORS } from '../../constants/map';
import celfinetLogo from '../../../../assets/LogoCelfinetACyientCompany_Color-1.png';

const MapLegend = ({ stats, total, colourMode, onColourModeChange, featureStats, hideInactive, onHideInactiveChange, activityStatus, onActivityStatusChange, tileLayer, onTileLayerChange, selectedFeatures }) => {
  return (
  <Box sx={{
    position: 'absolute', bottom: 48, left: 16, zIndex: 1200,
    backgroundColor: 'rgba(255,255,255,0.97)', borderRadius: 2,
    px: 2, py: 1.5, border: '1px solid #CBD5E0',
    fontFamily: '"IBM Plex Mono", monospace',
    minWidth: 160, pointerEvents: 'all',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  }}>

    {/* Map Layer selector */}
    <Box sx={{ mb: 0.8, pb: 0.8, borderBottom: '1px solid #E2E8F0' }}>
      <Typography sx={{ color: '#94A3B8', fontSize: 9, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
        Map Layer
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        {[
          { id: 'light',     label: 'Light'     },
          { id: 'dark',      label: 'Dark'      },
          { id: 'satellite', label: 'Satellite' },
        ].map(function(layer) {
          var active = tileLayer === layer.id;
          return (
            <Box key={layer.id} onClick={function() { onTileLayerChange(layer.id); }}
              sx={{
                flex: 1, textAlign: 'center', cursor: 'pointer', borderRadius: 1, py: 0.4, px: 0.5,
                backgroundColor: active ? '#1565C0' : 'transparent',
                border: active ? '1px solid #1565C0' : '1px solid #CBD5E0',
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#1565C0' },
              }}>
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: active ? '#fff' : '#64748B', letterSpacing: 0.5, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                {layer.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>

    {/* Colour By toggle */}
    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.8, pb: 0.8, borderBottom: '1px solid #E2E8F0' }}>
      {['status', 'feature'].map(function(mode) {
        var active = colourMode === mode;
        return (
          <Box key={mode} onClick={function() { onColourModeChange(mode); }}
            sx={{
              flex: 1, textAlign: 'center', cursor: 'pointer',
              px: 1, py: 0.4, borderRadius: 1, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'inherit',
              backgroundColor: active ? '#1565C0' : 'transparent',
              color: active ? '#fff' : '#64748B',
              border: '1px solid ' + (active ? '#1565C0' : '#CBD5E0'),
              transition: 'all 0.15s',
              '&:hover': { borderColor: '#1565C0', color: '#1565C0' },
            }}>
            {mode === 'status' ? 'Status' : 'Feature'}
          </Box>
        );
      })}
    </Box>

    {/* Activity Status selector */}
    <Box sx={{ display: 'flex', gap: 0.5, mb: 0.6, pb: 0.6, borderBottom: '1px solid #E2E8F0' }}>
      {['Open', 'Closed'].map(function(s) {
        var active = activityStatus === s;
        var col    = s === 'Closed' ? '#BF360C' : '#1565C0';
        return (
          <Box key={s} onClick={function() { onActivityStatusChange(s); }}
            sx={{
              flex: 1, textAlign: 'center', cursor: 'pointer',
              px: 1, py: 0.4, borderRadius: 1, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: 'inherit',
              backgroundColor: active ? col + '18' : 'transparent',
              color: active ? col : '#64748B',
              border: '1px solid ' + (active ? col : '#CBD5E0'),
              transition: 'all 0.15s',
              '&:hover': { borderColor: col, color: col },
            }}>
            {s}
          </Box>
        );
      })}
    </Box>

    {/* Active Only toggle */}
    <Box onClick={function() { onHideInactiveChange(!hideInactive); }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 1.2, pb: 1,
        borderBottom: '1px solid #E2E8F0', cursor: 'pointer',
        '&:hover .hide-label': { color: '#1565C0' },
      }}>
      <Box sx={{
        width: 28, height: 14, borderRadius: 7, flexShrink: 0,
        backgroundColor: hideInactive ? '#1565C0' : '#CBD5E0',
        border: '1px solid ' + (hideInactive ? '#1565C0' : '#94A3B8'),
        position: 'relative', transition: 'all 0.15s',
      }}>
        <Box sx={{
          position: 'absolute', top: 1, left: hideInactive ? 15 : 1,
          width: 10, height: 10, borderRadius: '50%',
          backgroundColor: '#fff', transition: 'left 0.15s',
        }} />
      </Box>
      <Typography className="hide-label" sx={{
        fontFamily: 'inherit', fontSize: 9, fontWeight: 700,
        color: hideInactive ? '#1565C0' : '#94A3B8',
        textTransform: 'uppercase', letterSpacing: 0.8, transition: 'color 0.15s',
      }}>
        Active only
      </Typography>
    </Box>

    {/* Site counts */}
    {total > 0 && (
      <Box sx={{ mb: 1.4, pb: 1.2, borderBottom: '1px solid #E2E8F0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.8 }}>
          <Typography sx={{ fontFamily: 'inherit', fontSize: 10, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>
            Sites
          </Typography>
          <Typography sx={{ fontFamily: 'inherit', fontSize: 13, fontWeight: 800, color: '#1565C0' }}>
            {total.toLocaleString()}
          </Typography>
        </Box>
        {colourMode === 'status' && [
          { key: 'inprogress', label: 'In Progress' },
          { key: 'planned',    label: 'Planned'      },
        ].filter(function(x) { return stats[x.key] > 0; }).map(function(x) {
          return (
            <Box key={x.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.3 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#64748B', letterSpacing: 0.5 }}>{x.label}</Typography>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: STATUS_COLORS[x.key] }}>{stats[x.key].toLocaleString()}</Typography>
            </Box>
          );
        })}
        {colourMode === 'feature' && Object.entries(featureStats).map(function(entry) {
          var name = entry[0]; var val = entry[1];
          return (
            <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.3 }}>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#64748B', letterSpacing: 0.5 }}>{name}</Typography>
              <Typography sx={{ fontFamily: 'inherit', fontSize: 11, fontWeight: 700, color: val.color }}>{val.count.toLocaleString()}</Typography>
            </Box>
          );
        })}
      </Box>
    )}

    {/* Legend dots */}
    <Typography sx={{ fontFamily: 'inherit', fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1.2, display: 'block', mb: 1 }}>
      {colourMode === 'status' ? 'Activity Status' : 'Feature'}
    </Typography>

    {colourMode === 'status' ? (
      (activityStatus === 'Closed' ? [
        { key: 'overdue', label: 'Closed / Expired', color: STATUS_COLORS.overdue || '#BF360C' },
        { key: 'none',    label: 'No Activity',      color: STATUS_COLORS.none    || '#546E7A' },
      ] : [
        { key: 'inprogress', label: 'In Progress', color: STATUS_COLORS.inprogress },
        { key: 'planned',    label: 'Planned',      color: STATUS_COLORS.planned    },
        { key: 'none',       label: 'No Activity',  color: STATUS_COLORS.none       },
      ]).map(function(x) {
        return (
          <Box key={x.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: x.color,
                       boxShadow: x.key !== 'none' ? '0 0 5px ' + x.color : 'none' }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569' }}>{x.label}</Typography>
          </Box>
        );
      })
    ) : Object.keys(featureStats).length === 0 ? (
      <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#94A3B8', fontStyle: 'italic' }}>No active features</Typography>
    ) : (
      Object.entries(featureStats).map(function(entry) {
        var name = entry[0]; var val = entry[1];
        return (
          <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                       backgroundColor: val.color, boxShadow: '0 0 5px ' + val.color }} />
            <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569',
                              maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </Typography>
          </Box>
        );
      }).concat(hideInactive ? [] : [
        <Box key="none" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, backgroundColor: STATUS_COLORS.none }} />
          <Typography sx={{ fontFamily: 'inherit', fontSize: 10, color: '#475569' }}>No Activity</Typography>
        </Box>
      ])
    )}

    {/* Logo */}
    <Box sx={{ mt: 1.2, pt: 1, borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'center' }}>
      <Box component="img" src={celfinetLogo} alt="Celfinet" sx={{ height: 36, width: 'auto' }} />
    </Box>
  </Box>
  );
};

MapLegend.defaultProps = { stats: {}, total: 0, featureStats: {}, hideInactive: false, activityStatus: 'Open' };

export default MapLegend;